import moment from 'moment';

/**
 * Generate all schedule dates between start and end, based on frequency.
 * @param {string|Date|moment} startDate
 * @param {string|Date|moment} endDate
 * @param {string} frequencyType - 'DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM'
 * @param {number|string|Array} frequencyValue - interval, dayOfWeek, dayOfMonth, or array of dates
 * @param {boolean} excludeToday - whether to exclude today's date from the results
 * @returns {string[]} Array of date strings in 'YYYY-MM-DD' format
 */
export function generateScheduleDates(startDate, endDate, frequencyType, frequencyValue, excludeToday = true) {
  const dates = [];
  let current = moment(startDate).startOf('day');
  const end = moment(endDate).endOf('day');
  const today = moment().format('YYYY-MM-DD');

  if (!startDate || !endDate || !frequencyType) return [];

  if (frequencyType === 'DAILY') {
    const interval = Number(frequencyValue) || 1;
    while (current.isSameOrBefore(end)) {
      const dateStr = current.format('YYYY-MM-DD');
      // Only add if it's not today (when excludeToday is true)
      if (!excludeToday || dateStr !== today) {
        dates.push(dateStr);
      }
      current = current.add(interval, 'days');
    }
  } else if (frequencyType === 'WEEKLY') {
    // frequencyValue is a day string, e.g. 'MONDAY'
    const daysOfWeek = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
    const dayOfWeek = daysOfWeek.indexOf(String(frequencyValue).toUpperCase());
    if (dayOfWeek === -1) return [];
    // Move to the first occurrence of the desired day
    current = current.day(dayOfWeek);
    if (current.isBefore(moment(startDate))) current.add(1, 'weeks');
    while (current.isSameOrBefore(end)) {
      const dateStr = current.format('YYYY-MM-DD');
      // Only add if it's not today (when excludeToday is true)
      if (!excludeToday || dateStr !== today) {
        dates.push(dateStr);
      }
      current = current.add(1, 'weeks');
    }
  } else if (frequencyType === 'MONTHLY') {
    // frequencyValue is day of month (1-28)
    const dayOfMonth = Number(frequencyValue);
    if (!dayOfMonth || dayOfMonth < 1 || dayOfMonth > 28) return [];
    // Move to the first occurrence of the desired day
    current = current.date(dayOfMonth);
    if (current.isBefore(moment(startDate))) current.add(1, 'months').date(dayOfMonth);
    while (current.isSameOrBefore(end)) {
      const dateStr = current.format('YYYY-MM-DD');
      // Only add if it's not today (when excludeToday is true)
      if (!excludeToday || dateStr !== today) {
        dates.push(dateStr);
      }
      current = current.add(1, 'months').date(dayOfMonth);
    }
  } else if (frequencyType === 'CUSTOM') {
    // frequencyValue is already an array of dates
    if (Array.isArray(frequencyValue)) {
      // Filter out today's date if excludeToday is true
      if (excludeToday) {
        return frequencyValue.filter(date => {
          const dateStr = moment(date).format('YYYY-MM-DD');
          return dateStr !== today;
        });
      }
      return frequencyValue;
    }
    return [];
  }
  return dates;
}